import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, SpeakerHigh, SpeakerSlash, X } from "@phosphor-icons/react";
import API from "../lib/api";

/* Live spotlight tour: dims the page, cuts a hole around the step's target (a data-testid selector), reads the step aloud.
   Start one from anywhere with startLiveTour("dashboard"); <LiveTourHost/> (mounted in Layout) does the rest. */
const EVENT = "fixlr:live-tour";
const MUTE_KEY = "fixlr_tour_muted";
const PAD = 8;
const CARD_W = 340;
export const TOUR_ROLES = ["super_admin", "admin", "project_manager"];

export function startLiveTour(key) {
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { key } }));
}

/* Goes to the dashboard (where the highlighted controls live) and starts the live tour. */
export function useLaunchLiveTour() {
  const navigate = useNavigate();
  const location = useLocation();
  return (key = "dashboard") => {
    const onDashboard = location.pathname === "/";
    if (!onDashboard) navigate("/");
    setTimeout(() => startLiveTour(key), onDashboard ? 0 : 400);
  };
}

function useTargetRect(selector, step) {
  const [rect, setRect] = useState(null);
  const measure = useCallback(() => {
    if (!selector) { setRect(null); return; }
    const el = document.querySelector(selector);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) { setRect(null); return; }
    const top = Math.max(PAD + 2, r.top), left = Math.max(PAD + 2, r.left);
    const bottom = Math.min(window.innerHeight - PAD - 2, r.bottom), right = Math.min(window.innerWidth - PAD - 2, r.right);
    setRect({ top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top) });
  }, [selector]);

  useLayoutEffect(() => {
    if (!selector) { setRect(null); return undefined; }
    const el = document.querySelector(selector);
    if (el) el.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
    const t1 = setTimeout(measure, 60);
    const t2 = setTimeout(measure, 450);
    const t3 = setTimeout(measure, 1200);
    const t4 = setTimeout(measure, 2500);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => { [t1, t2, t3, t4].forEach(clearTimeout); window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
  }, [selector, step, measure]);
  return rect;
}

function cardPosition(rect, placement) {
  const vw = window.innerWidth, vh = window.innerHeight;
  const w = Math.min(CARD_W, vw - 24);
  if (!rect) return { top: Math.max(16, vh / 2 - 140), left: vw / 2 - w / 2, width: w };
  const gap = 14;
  let top, left;
  const fits = { bottom: rect.top + rect.height + gap + 240 < vh, top: rect.top - gap - 240 > 0, right: rect.left + rect.width + gap + w < vw, left: rect.left - gap - w > 0 };
  const order = [placement, "bottom", "right", "top", "left"].filter(Boolean);
  const p = order.find((k) => fits[k]) || "bottom";
  if (p === "bottom") { top = rect.top + rect.height + gap; left = rect.left + rect.width / 2 - w / 2; }
  else if (p === "top") { top = rect.top - gap - 240; left = rect.left + rect.width / 2 - w / 2; }
  else if (p === "right") { top = rect.top; left = rect.left + rect.width + gap; }
  else { top = rect.top; left = rect.left - gap - w; }
  return { top: Math.max(12, Math.min(top, vh - 250)), left: Math.max(12, Math.min(left, vw - w - 12)), width: w };
}

export function LiveTour({ tour, onClose }) {
  const [i, setI] = useState(0);
  const [muted, setMuted] = useState(() => localStorage.getItem(MUTE_KEY) === "1");
  const audioRef = useRef(null);
  const steps = tour.steps || [];
  const step = steps[i];
  const rect = useTargetRect(step?.target, i);
  const pos = cardPosition(rect, step?.placement);

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !step) return;
    if (step.audio && !muted) { a.src = step.audio; a.play().catch(() => {}); } else { a.pause(); }
  }, [i, step, muted]);
  useEffect(() => { localStorage.setItem(MUTE_KEY, muted ? "1" : "0"); }, [muted]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose(false);
      if (e.key === "ArrowRight") setI((n) => Math.min(steps.length - 1, n + 1));
      if (e.key === "ArrowLeft") setI((n) => Math.max(0, n - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [steps.length, onClose]);

  if (!step) return null;
  const last = i === steps.length - 1;

  return createPortal(
    <div className="fixed inset-0 z-[90]" data-testid="live-tour" role="dialog" aria-label={tour.title}>
      <div className="absolute inset-0" onClick={() => onClose(false)} data-testid="live-tour-backdrop" />
      {rect ? (
        <div
          className="absolute rounded-lg ring-2 ring-primary pointer-events-none transition-all duration-300"
          style={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2, boxShadow: "0 0 0 9999px rgba(5,7,10,0.72)" }}
          data-testid="live-tour-spotlight"
        />
      ) : (
        <div className="absolute inset-0 bg-[rgba(5,7,10,0.72)] pointer-events-none" />
      )}
      <div
        className="absolute rounded-xl border border-border bg-card text-card-foreground shadow-2xl p-4 animate-in fade-in zoom-in-95 duration-200"
        style={{ top: pos.top, left: pos.left, width: pos.width }}
        onClick={(e) => e.stopPropagation()}
        data-testid="live-tour-card"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-primary font-semibold" data-testid="live-tour-step-counter">Step {i + 1} of {steps.length}</p>
            <h3 className="text-base font-bold font-[Chivo] mt-0.5" data-testid="live-tour-step-title">{step.title}</h3>
          </div>
          <button type="button" onClick={() => onClose(false)} className="p-1 rounded hover:bg-muted text-muted-foreground" aria-label="Close tour" data-testid="live-tour-close"><X size={16} /></button>
        </div>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed" data-testid="live-tour-step-text">{step.text}</p>
        {step.target && !rect && (
          <p className="text-[11px] text-amber-600 mt-2" data-testid="live-tour-missing-target">This control isn't on your screen right now (it may be hidden on small screens).</p>
        )}
        <div className="flex items-center gap-1 mt-4">
          {tour.narrated && (
            <button type="button" onClick={() => setMuted((m) => !m)} className="p-1.5 rounded hover:bg-muted text-muted-foreground" aria-label={muted ? "Unmute narration" : "Mute narration"} data-testid="live-tour-mute">
              {muted ? <SpeakerSlash size={16} /> : <SpeakerHigh size={16} />}
            </button>
          )}
          <div className="flex-1 flex items-center gap-1 px-2">
            {steps.map((s, n) => (
              <span key={n} className={`h-1 flex-1 rounded-full ${n <= i ? "bg-primary" : "bg-muted"}`} />
            ))}
          </div>
          <button type="button" onClick={() => setI((n) => Math.max(0, n - 1))} disabled={i === 0} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 h-8 rounded-md border border-border hover:bg-muted disabled:opacity-40" data-testid="live-tour-back">
            <ArrowLeft size={12} /> Back
          </button>
          {last ? (
            <button type="button" onClick={() => onClose(true)} className="inline-flex items-center gap-1 text-xs font-semibold px-3 h-8 rounded-md bg-primary text-primary-foreground hover:opacity-90" data-testid="live-tour-finish">Done</button>
          ) : (
            <button type="button" onClick={() => setI((n) => n + 1)} className="inline-flex items-center gap-1 text-xs font-semibold px-3 h-8 rounded-md bg-primary text-primary-foreground hover:opacity-90" data-testid="live-tour-next">
              Next <ArrowRight size={12} />
            </button>
          )}
        </div>
      </div>
      <audio ref={audioRef} preload="auto" data-testid="live-tour-audio" />
    </div>,
    document.body,
  );
}

/* Listens for startLiveTour(key), loads the steps and runs the tour; marks it seen when finished or dismissed. */
export function LiveTourHost() {
  const [tour, setTour] = useState(null);
  useEffect(() => {
    const onStart = async (e) => {
      try {
        const { data } = await API.get(`/kb/tours/${encodeURIComponent(e.detail.key)}`);
        setTour(data);
      } catch { /* role without this tour */ }
    };
    window.addEventListener(EVENT, onStart);
    return () => window.removeEventListener(EVENT, onStart);
  }, []);
  if (!tour) return null;
  const close = () => {
    API.post(`/kb/tours/${tour.key}/seen`).catch(() => {});
    setTour(null);
  };
  return <LiveTour tour={tour} onClose={close} />;
}
